import {
  DoctorHospital,
  MedicalCredential,
  MinimalDoctorDto,
  UserAppSettings,
  UserConfig
} from '@/modules/doctorBff.model';
import axios from 'axios';
import { ApiLanguage } from '@ianesthesie-kit/config';
import omit from 'lodash/fp/omit';
import {
  AuthService,
  ID,
  ListApiResponse,
  Medium,
  SingleApiResponse,
  TokenResponse
} from '@ianesthesie-kit/services';

export class DoctorBffService {
  private static readonly URL = '/bff/doctor';

  public static fetchCredentials(): Promise<
    ListApiResponse<MedicalCredential>
  > {
    return axios
      .get<ListApiResponse<MedicalCredential>>(this.url('/medical-credentials'))
      .then((resp) => resp.data);
  }

  public static fetchDoctorsOfCurrentHospital(): Promise<MinimalDoctorDto[]> {
    return axios.get(this.url()).then((resp) => resp.data.data);
  }

  public static fetchDoctorOfCurrentHospitalById(
    doctorId: ID
  ): Promise<MinimalDoctorDto> {
    return axios.get(this.url(doctorId)).then((resp) => resp.data.data);
  }

  public static getUserConfig(): Promise<UserConfig> {
    return axios.get(this.url('/config')).then((resp) => resp.data.data);
  }

  public static updateUserAppSettings(
    settings: UserAppSettings | undefined
  ): Promise<UserConfig> {
    // solves bug https://app.clickup.com/t/860rmv1e4
    const normalizedSettings = omit(
      ['settings', 'language', 'marketingCommsStatus'],
      settings
    );
    return axios
      .put(this.url('/config/app-settings'), normalizedSettings ?? null)
      .then((resp) => resp.data.data);
  }

  public static updateUserLanguage(lang: ApiLanguage): Promise<UserConfig> {
    return axios
      .put(this.url(`/config/language?language=${lang}`))
      .then((resp) => resp.data.data);
  }

  public static fetchDoctorHospitals(): Promise<DoctorHospital[]> {
    return axios
      .get<ListApiResponse<DoctorHospital>>('/auth/me/doctor-hospital')
      .then((resp) => resp.data.data.filter((dh) => !!dh.hospital));
  }

  public static setActiveDoctorHospital(
    doctorHospital: DoctorHospital | ID
  ): Promise<boolean> {
    if (!doctorHospital) {
      throw new Error('No doctor hospital selected');
    }

    const id =
      typeof doctorHospital === 'string' ? doctorHospital : doctorHospital.id;
    return axios
      .put<SingleApiResponse<TokenResponse>>(
        `/auth/me/doctor-hospital/${id}/active`
      )
      .then((resp) => {
        const newTokensResp = resp.data.data;
        const newToken = newTokensResp.accessToken;
        const oldToken = AuthService.getInstance().getAccessToken();
        const updateToken = !!newToken && newToken !== oldToken;
        if (updateToken) {
          AuthService.getInstance().setTokens(newTokensResp);
        }

        return updateToken;
      });
  }

  // TODO this should be moved in MediaService in kit
  public static fetchMediumById(id: ID): Promise<Medium> {
    return axios
      .get<SingleApiResponse<Medium>>(this.url(`/medium/${id}`))
      .then((resp) => resp.data.data);
  }

  private static url(seg?: string): string {
    if (!seg) {
      return this.URL;
    }

    return `${this.URL}/${seg}`.replace('//', '/');
  }
}
